import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { CustomerHealthRow, FailureKind, LimitMode, RetiredModelRow, adminInsightsApi } from '../services/adminInsightsApi';
import { SectionTitle, StatCard, fmtBucket, fmtCredits, fmtMs, fmtNum, fmtPct, fmtS, healthColor } from '../components/performance/common';
import { AccuracyTab } from '../components/tests/AccuracyTab';

type TabKey = 'retired' | 'providers' | 'customers' | 'limits' | 'accuracy';
type Api = ReturnType<typeof adminInsightsApi>;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'retired', label: 'Retired models' },
  { key: 'providers', label: 'Provider health' },
  { key: 'customers', label: 'Customer health' },
  { key: 'limits', label: 'Time limits' },
  { key: 'accuracy', label: 'Accuracy & models' },
];
const WINDOWS = [1, 7, 30];

const KIND_COLORS: Record<FailureKind, string> = {
  rate_limit: '#ffb74d',
  not_servable: '#ba68c8',
  timeout: '#e57373',
  server: '#b71c1c',
  auth: '#64b5f6',
  other: '#9e9e9e',
};

const MODE_LABELS: Record<LimitMode, string> = {
  off: 'Off',
  fixed: 'Fixed',
  auto: 'Automatic',
  chain: 'From chain',
  none: 'No settings',
};

const functionPath = (customerId: string, customerTag: string | null, functionId: string) => {
  const params = new URLSearchParams({ tab: 'performance', customer_id: customerId });
  if (customerTag) params.set('customer_tag', customerTag);
  return `/dashboard/function-runs/${functionId}?${params}`;
};

const chainPath = (customerId: string, chainId: string) => `/dashboard/chain-performance/${chainId}?customer_id=${encodeURIComponent(customerId)}`;

const customerRunsPath = (customerId: string, customerTag: string | null) => {
  const params = new URLSearchParams({ customer_id: customerId });
  if (customerTag) params.set('customer_tag', customerTag);
  return `/dashboard/function-runs?${params}`;
};

const fmtWhen = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '–');

const fmtPts = (delta?: number | null) => (delta === undefined || delta === null ? '–' : `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(1)} pts`);

const fmtChange = (ratio?: number | null) => (ratio === undefined || ratio === null ? 'new' : `${ratio >= 0 ? '+' : ''}${Math.round(ratio * 100)}%`);

/** Red when a rise is bad and it rose, green when it fell. */
const deltaColor = (delta?: number | null, threshold = 0) =>
  delta === undefined || delta === null || Math.abs(delta) <= threshold ? 'text.secondary' : delta > 0 ? 'error.main' : 'success.main';

const Delta: React.FC<{ value: React.ReactNode; delta: string; color: string }> = ({ value, delta, color }) => (
  <Box sx={{ whiteSpace: 'nowrap' }}>
    <Typography variant="body2" component="span">
      {value}
    </Typography>
    <Typography variant="caption" component="div" sx={{ color }}>
      {delta}
    </Typography>
  </Box>
);

const Empty: React.FC<{ colSpan: number; children: React.ReactNode }> = ({ colSpan, children }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>
      {children}
    </TableCell>
  </TableRow>
);

/** Loads one tab's data for the selected window; refetches on `reload`. */
function useInsight<T>(load: (days: number) => Promise<T>, days: number, reload: number) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load(days)
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [load, days, reload]);
  return { data, loading, error };
}

const TabFrame: React.FC<{ loading: boolean; error: string | null; hasData: boolean; truncated?: boolean; children: React.ReactNode }> = ({
  loading,
  error,
  hasData,
  truncated,
  children,
}) => (
  <Box>
    {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )}
    {truncated && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Showing the largest rows only; narrow the window to see everything.
      </Alert>
    )}
    {!hasData && loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    ) : (
      <Box sx={{ opacity: loading ? 0.6 : 1 }}>{children}</Box>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Retired models
// ---------------------------------------------------------------------------

const retiredTarget = (r: RetiredModelRow) =>
  r.kind === 'chain' ? (r.chain_id ? chainPath(r.customer_id, r.chain_id) : null) : r.function_id ? functionPath(r.customer_id, r.customer_tag, r.function_id) : null;

const RetiredTab: React.FC<{ api: Api; days: number; reload: number }> = ({ api, days, reload }) => {
  const navigate = useNavigate();
  const { data, loading, error } = useInsight(api.retiredModels, days, reload);
  const rows = data?.rows ?? [];
  return (
    <TabFrame loading={loading} error={error} hasData={!!data} truncated={data?.truncated}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Functions and chain steps that still ask for a retired model. EasyAI quietly swaps in the replacement, so these calls work, but the customer is
        not getting the model they picked and should update their configuration. Rows come from runs in the window (what was asked for before the swap)
        and from saved configuration (allowed models, chain step overrides) even without recent runs.
      </Typography>
      {data && data.models.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          {data.models.map(m => (
            <Tooltip key={m.model} title={`Retired ${m.sunset_date || ''}. ${m.customers} customers, ${m.targets} functions/steps, ${m.in_config} still in saved config.`}>
              <Chip size="small" variant="outlined" color="warning" label={`${m.model} → ${m.replacement || '?'} · ${fmtNum(m.runs)} runs`} />
            </Tooltip>
          ))}
        </Stack>
      )}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Function or chain</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Retired → replacement</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Last run</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <Empty colSpan={5}>{data ? 'No function or chain asked for a retired model in this window, and none has one saved.' : ''}</Empty>
              ) : (
                rows.map(r => {
                  const to = retiredTarget(r);
                  const key = `${r.kind}:${r.customer_id}:${r.chain_id || r.name}:${r.node_id || ''}:${r.retired_model}`;
                  return (
                    <TableRow key={key} hover onClick={() => to && navigate(to)} sx={{ cursor: to ? 'pointer' : 'default' }}>
                      <TableCell>
                        {to ? (
                          <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 500 }} onClick={e => e.stopPropagation()}>
                            {r.name}
                          </Link>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {r.name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="div">
                          {[r.kind === 'chain' ? `Chain step: ${r.step || r.node_id}` : 'Function', r.config_detail && `saved as ${r.config_detail}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.customer_tag || r.customer_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {r.retired_model} → {r.replacement || '?'}
                        </Typography>
                        {(r.image_replacement || (r.answered_by && r.answered_by !== r.replacement)) && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {[r.image_replacement && `images: ${r.image_replacement}`, r.answered_by && r.answered_by !== r.replacement && `mostly answered by ${r.answered_by}`]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.runs ? (
                          <Tooltip title={`${fmtNum(r.ok)} succeeded`}>
                            <span>{fmtNum(r.runs)}</span>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            config only
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {fmtWhen(r.last_run)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </TabFrame>
  );
};

// ---------------------------------------------------------------------------
// Provider health
// ---------------------------------------------------------------------------

const ProvidersTab: React.FC<{ api: Api; days: number; reload: number }> = ({ api, days, reload }) => {
  const { data, loading, error } = useInsight(api.providers, days, reload);
  const [provider, setProvider] = useState<string>('all');
  const kinds = data?.kinds ?? [];

  const chart = useMemo(() => {
    if (!data) return [];
    const byBucket = new Map<string, Record<string, number | string | null>>();
    data.series
      .filter(s => provider === 'all' || s.provider === provider)
      .forEach(s => {
        const row = byBucket.get(s.bucket) ?? { bucket: s.bucket, label: fmtBucket(s.bucket, data.window.unit), calls: 0, failures: 0 };
        row.calls = (row.calls as number) + s.calls;
        row.failures = (row.failures as number) + s.failures;
        Object.entries(s.kinds).forEach(([k, n]) => {
          row[k] = ((row[k] as number) || 0) + n;
        });
        byBucket.set(s.bucket, row);
      });
    return Array.from(byBucket.values())
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
      .map(r => ({ ...r, rate: r.calls ? Math.round(((r.failures as number) / (r.calls as number)) * 1000) / 10 : null }));
  }, [data, provider]);

  const models = (data?.models ?? []).filter(m => m.failures > 0 && (provider === 'all' || m.provider === provider)).slice(0, 15);

  return (
    <TabFrame loading={loading} error={error} hasData={!!data} truncated={data?.truncated}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every model call across all customers, including failed attempts a backup model recovered from. Failures are grouped by kind from the attempt&apos;s
        outcome and error text (usage-only runs keep no error text, so their provider errors count as 5xx). Region comes from the routing rules for
        Claude and Gemini on Vertex and the account&apos;s current ZDR setting; it is n/a for other providers and for calls that asked for ZDR by header.
      </Typography>

      {data && data.provider_wide.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {data.provider_wide.map(w => (
            <Alert key={`${w.provider}:${w.start}`} severity={w.state === 'down' ? 'error' : 'warning'}>
              <strong>
                {w.provider} failing across models{w.ongoing ? ' (ongoing)' : ''}:
              </strong>{' '}
              {fmtWhen(w.start)} – {fmtWhen(w.end)} ({w.minutes} min). {w.models.length} models affected: {w.models.join(', ')}.
            </Alert>
          ))}
        </Stack>
      )}

      {data && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Failures over time"
              help="Failed model calls by kind (bars) and the share of calls that failed (line)."
              action={
                <ToggleButtonGroup size="small" exclusive value={provider} onChange={(_, v) => v && setProvider(v)} sx={{ flexWrap: 'wrap' }}>
                  <ToggleButton value="all">All</ToggleButton>
                  {data.providers.map(p => (
                    <ToggleButton key={p.provider} value={p.provider}>
                      {p.provider}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              }
            />
            {chart.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No model calls in this window.
              </Typography>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chart} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                    <YAxis yAxisId="n" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                    <ChartTooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {kinds.map(k => (
                      <Bar key={k.key} yAxisId="n" dataKey={k.key} name={k.label} stackId="f" fill={KIND_COLORS[k.key]} />
                    ))}
                    <Line yAxisId="r" dataKey="rate" name="Failed (% of calls)" stroke="#424242" dot={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle title="By provider and region" help="Region rows show where calls were routed: standard, ZDR without residency (fastest), US-pinned ZDR, or ZDR asked for by the caller." />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider / region</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    {kinds.map(k => (
                      <TableCell key={k.key} align="right">
                        <Tooltip title={k.label}>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: KIND_COLORS[k.key] }} />
                            {k.label.split(' ')[0]}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.providers.length === 0 ? (
                    <Empty colSpan={3 + kinds.length}>No model calls in this window.</Empty>
                  ) : (
                    data.providers.flatMap(p => [
                      <TableRow key={p.provider} hover selected={provider === p.provider} onClick={() => setProvider(provider === p.provider ? 'all' : p.provider)} sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {p.provider}
                          <Typography variant="caption" color="text.secondary" component="div">
                            {p.models} models
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtNum(p.calls)}</TableCell>
                        <TableCell align="right" sx={{ color: (p.failure_rate || 0) >= 0.05 ? 'error.main' : undefined }}>
                          {fmtPct(p.failure_rate)}
                        </TableCell>
                        {kinds.map(k => (
                          <TableCell key={k.key} align="right">
                            {p.kinds[k.key] ? fmtNum(p.kinds[k.key]) : '–'}
                          </TableCell>
                        ))}
                      </TableRow>,
                      ...(p.regions.length > 1 || p.regions.some(r => r.region !== 'n/a')
                        ? p.regions.map(r => (
                            <TableRow key={`${p.provider}:${r.region}:${r.route}`}>
                              <TableCell sx={{ pl: 4, color: 'text.secondary' }}>
                                {r.region} · {r.route_label}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {fmtNum(r.calls)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {fmtPct(r.failure_rate)}
                              </TableCell>
                              {kinds.map(k => (
                                <TableCell key={k.key} align="right" sx={{ color: 'text.secondary' }}>
                                  {r.kinds[k.key] ? fmtNum(r.kinds[k.key]) : '–'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        : []),
                    ])
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle title="Models with the most failures" help={provider === 'all' ? 'All providers.' : `Provider: ${provider}.`} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell>Mostly</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.length === 0 ? (
                    <Empty colSpan={4}>No failed calls.</Empty>
                  ) : (
                    models.map(m => {
                      const top = (Object.entries(m.kinds) as [FailureKind, number][]).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 2);
                      return (
                        <TableRow key={m.model}>
                          <TableCell>
                            {m.model}
                            <Typography variant="caption" color="text.secondary" component="div">
                              {m.provider}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtNum(m.calls)}</TableCell>
                          <TableCell align="right">
                            {fmtNum(m.failures)}
                            <Typography variant="caption" color="text.secondary" component="div">
                              {fmtPct(m.failure_rate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                              {top.map(([k, n]) => (
                                <Chip key={k} size="small" variant="outlined" label={`${kinds.find(x => x.key === k)?.label.split(' ')[0] ?? k} ${fmtNum(n)}`} sx={{ borderColor: KIND_COLORS[k] }} />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle title="Incident timeline" help="Degraded and down periods from the per-minute model health snapshots (kept 30 days), newest first." />
              {!data.health_history_available ? (
                <Typography variant="body2" color="text.secondary">
                  No unhealthy snapshots in this window.
                </Typography>
              ) : (
                <Stack spacing={0.75} sx={{ maxHeight: 420, overflow: 'auto' }}>
                  {data.incidents
                    .filter(i => provider === 'all' || i.provider === provider)
                    .map(i => (
                      <Box key={`${i.model}:${i.start}`} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                        <Chip size="small" label={i.state} color={healthColor(i.state)} sx={{ minWidth: 76, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {i.model}
                            {i.provider_wide && <Chip size="small" label="provider-wide" color="error" variant="outlined" sx={{ ml: 1 }} />}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {fmtWhen(i.start)} – {i.ongoing ? 'now' : fmtWhen(i.end)} ({i.minutes} min){i.reasons.length ? ` · ${i.reasons.join('; ')}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Stack>
              )}
            </Paper>
          </Box>
        </>
      )}
    </TabFrame>
  );
};

// ---------------------------------------------------------------------------
// Customer health
// ---------------------------------------------------------------------------

const CustomersTab: React.FC<{ api: Api; days: number; reload: number }> = ({ api, days, reload }) => {
  const navigate = useNavigate();
  const { data, loading, error } = useInsight(api.customers, days, reload);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const rows = (data?.customers ?? []).filter((r: CustomerHealthRow) => !onlyFlagged || r.regressions.length > 0);
  const period = days === 1 ? 'day' : `${days} days`;

  return (
    <TabFrame loading={loading} error={error} hasData={!!data} truncated={data?.truncated}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every account&apos;s last {period} against the {period} before, biggest regressions first. Changes only count once both periods have{' '}
        {data?.min_runs ?? 20} runs. &quot;With warnings&quot; counts functions and chains over the warning thresholds in the last{' '}
        {data?.warning_window_days ?? 7} days, whatever the window. Click a row for the customer&apos;s runs.
      </Typography>
      <Paper variant="outlined">
        <Box sx={{ px: 2, pt: 1 }}>
          <FormControlLabel control={<Switch size="small" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} />} label="Only flagged customers" />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Backup</TableCell>
                <TableCell align="right">p95</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <Empty colSpan={7}>{data ? (onlyFlagged ? 'No customer regressed against the previous period.' : 'No runs in this window or the one before.') : ''}</Empty>
              ) : (
                rows.map(r => {
                  const runsPath = customerRunsPath(r.customer_id, r.customer_tag);
                  return (
                    <TableRow key={r.customer_id} hover onClick={() => navigate(runsPath)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Link component={RouterLink} to={runsPath} underline="hover" sx={{ fontWeight: 500 }} onClick={e => e.stopPropagation()}>
                          {r.customer_tag || r.customer_id}
                        </Link>
                        <Typography variant="caption" component="div">
                          <Link component={RouterLink} to={`/dashboard/insights?customer_id=${encodeURIComponent(r.customer_id)}`} underline="hover" onClick={e => e.stopPropagation()}>
                            Insights
                          </Link>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Delta value={fmtNum(r.runs)} delta={r.prev_runs ? `${fmtChange(r.runs_change)} vs ${fmtNum(r.prev_runs)}` : 'new'} color="text.secondary" />
                      </TableCell>
                      <TableCell align="right">
                        <Delta value={fmtPct(r.error_rate)} delta={fmtPts(r.error_rate_delta)} color={deltaColor(r.error_rate_delta, 0.005)} />
                      </TableCell>
                      <TableCell align="right">
                        <Delta value={fmtPct(r.backup_share)} delta={fmtPts(r.backup_share_delta)} color={deltaColor(r.backup_share_delta, 0.01)} />
                      </TableCell>
                      <TableCell align="right">
                        <Delta
                          value={fmtMs(r.p95_ms)}
                          delta={r.p95_delta_ms === null ? '–' : `${r.p95_delta_ms >= 0 ? '+' : '-'}${fmtMs(Math.abs(r.p95_delta_ms))}`}
                          color={deltaColor(r.p95_delta_ms, 500)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Delta value={fmtCredits(r.credits)} delta={`${r.credits_delta >= 0 ? '+' : ''}${fmtCredits(r.credits_delta)}`} color="text.secondary" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                          {r.regressions.map(g => (
                            <Chip
                              key={g.code}
                              size="small"
                              variant="outlined"
                              label={g.label}
                              color={g.code === 'errors_up' || g.code === 'high_errors' || g.code === 'runs_down' ? 'error' : 'warning'}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </TabFrame>
  );
};

// ---------------------------------------------------------------------------
// Time limits
// ---------------------------------------------------------------------------

const modeLabel = (mode: LimitMode, preset: string | null) => `${MODE_LABELS[mode] ?? mode}${preset ? ` (${preset})` : ''}`;

const headroomColor = (h: number | null) => (h === null ? 'text.secondary' : h < 0 ? 'error.main' : h < 2 ? 'warning.main' : 'success.main');

const LimitCell: React.FC<{ now: number | null; applied: number | null; note: string }> = ({ now, applied, note }) => (
  <Tooltip title={note}>
    <Box sx={{ whiteSpace: 'nowrap', cursor: 'help' }}>
      <Typography variant="body2" component="span">
        {now !== null ? fmtS(now) : 'none'}
      </Typography>
      {applied !== null && applied !== now && (
        <Typography variant="caption" color="text.secondary" component="div">
          {fmtS(applied)} applied
        </Typography>
      )}
    </Box>
  </Tooltip>
);

const HeadroomCell: React.FC<{ p95: number | null; headroom: number | null }> = ({ p95, headroom }) => (
  <Box sx={{ whiteSpace: 'nowrap' }}>
    <Typography variant="body2" component="span">
      {fmtMs(p95)}
    </Typography>
    <Typography variant="caption" component="div" sx={{ color: headroomColor(headroom) }}>
      {headroom === null ? '–' : headroom < 0 ? `${fmtS(-headroom)} over` : `${fmtS(headroom)} spare`}
    </Typography>
  </Box>
);

const LimitsTab: React.FC<{ api: Api; days: number; reload: number }> = ({ api, days, reload }) => {
  const navigate = useNavigate();
  const { data, loading, error } = useInsight(api.timeLimits, days, reload);
  const o = data?.overall;

  return (
    <TabFrame loading={loading} error={error} hasData={!!data} truncated={data?.truncated}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Runs with reliability settings (a time limit, retries or backup models) and how often an attempt ran out of time. A cut-off is rescued when a
        later attempt on a different model succeeded; it failed when the run still errored. &quot;Limit now&quot; is the limit the settings give today;
        &quot;applied&quot; is the typical limit recorded on runs in the window. Headroom is the limit minus the p95 of successful runs.
      </Typography>

      {o && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          <StatCard label="Function runs with settings" value={fmtNum(o.runs)} caption={`${fmtNum(o.limited)} with a time limit`} />
          <StatCard label="Cut off" value={fmtNum(o.cut_offs)} caption={`${fmtPct(o.cut_off_share)} of runs`} />
          <StatCard label="Rescued by a backup" value={fmtPct(o.rescue_share)} caption={`${fmtNum(o.rescued_backup)} runs; ${fmtNum(o.recovered_same_model)} more on the same model`} />
          <StatCard label="Failed after cut-off" value={fmtPct(o.failed_after_share)} caption={`${fmtNum(o.failed_after_cutoff)} runs`} />
          <StatCard
            label="Chains cut off"
            value={fmtNum(data?.chain_overall.cut_offs)}
            caption={`${fmtPct(data?.chain_overall.cut_off_share)} of ${fmtNum(data?.chain_overall.runs)} runs`}
          />
        </Box>
      )}

      {data && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle title="By mode and preset" help="Functions grouped by their saved time-limit setting." />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Functions</TableCell>
                  <TableCell align="right">Runs</TableCell>
                  <TableCell align="right">Cut off</TableCell>
                  <TableCell align="right">Rescued by backup</TableCell>
                  <TableCell align="right">Failed after cut-off</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_mode.length === 0 ? (
                  <Empty colSpan={6}>No runs with reliability settings in this window.</Empty>
                ) : (
                  data.by_mode.map(m => (
                    <TableRow key={`${m.mode}:${m.preset}`}>
                      <TableCell>{modeLabel(m.mode, m.preset)}</TableCell>
                      <TableCell align="right">{fmtNum(m.functions)}</TableCell>
                      <TableCell align="right">{fmtNum(m.runs)}</TableCell>
                      <TableCell align="right">
                        {fmtNum(m.cut_offs)} ({fmtPct(m.cut_off_share)})
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(m.rescued_backup)} ({fmtPct(m.rescue_share)})
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(m.failed_after_cutoff)} ({fmtPct(m.failed_after_share)})
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle title="Functions" help="Most cut-offs first. Click a row for the function's performance page." />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Function</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">Limit now</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Cut off</TableCell>
                    <TableCell align="right">Rescued</TableCell>
                    <TableCell align="right">Failed after</TableCell>
                    <TableCell align="right">p95 / headroom</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.functions.length === 0 ? (
                    <Empty colSpan={8}>No function ran with reliability settings in this window.</Empty>
                  ) : (
                    data.functions.map(f => {
                      const to = f.function_id ? functionPath(f.customer_id, f.customer_tag, f.function_id) : null;
                      return (
                        <TableRow key={`${f.customer_id}:${f.function_name}:${f.family_id}`} hover onClick={() => to && navigate(to)} sx={{ cursor: to ? 'pointer' : 'default' }}>
                          <TableCell>
                            {to ? (
                              <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 500 }} onClick={e => e.stopPropagation()}>
                                {f.function_name}
                              </Link>
                            ) : (
                              f.function_name
                            )}
                            <Typography variant="caption" color="text.secondary" component="div">
                              {f.customer_tag || f.customer_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{modeLabel(f.mode, f.preset)}</TableCell>
                          <TableCell align="right">
                            <LimitCell now={f.limit_now_s} applied={f.applied_limit_s} note={f.limit_note} />
                          </TableCell>
                          <TableCell align="right">{fmtNum(f.runs)}</TableCell>
                          <TableCell align="right">
                            <Delta value={fmtNum(f.cut_offs)} delta={fmtPct(f.cut_off_share)} color={(f.cut_off_share || 0) >= 0.05 ? 'error.main' : 'text.secondary'} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={f.recovered_same_model ? `${fmtNum(f.recovered_same_model)} more recovered on the same model` : ''}>
                              <span>
                                <Delta value={fmtNum(f.rescued_backup)} delta={fmtPct(f.rescue_share)} color="text.secondary" />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Delta value={fmtNum(f.failed_after_cutoff)} delta={fmtPct(f.failed_after_share)} color={f.failed_after_cutoff ? 'error.main' : 'text.secondary'} />
                          </TableCell>
                          <TableCell align="right">
                            <HeadroomCell p95={f.p95_ms} headroom={f.headroom_s} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle
              title="Chains"
              help="Chains with a time limit set, or that hit one ('Chain time limit of Xs reached'). Backup counts completed runs where a step was answered by a backup model."
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chain</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">Limit now</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Cut off</TableCell>
                    <TableCell align="right">Step used backup</TableCell>
                    <TableCell align="right">p95 / headroom</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.chains.length === 0 ? (
                    <Empty colSpan={7}>No chain had a time limit or hit one in this window.</Empty>
                  ) : (
                    data.chains.map(c => {
                      const to = chainPath(c.customer_id, c.chain_id);
                      return (
                        <TableRow key={c.chain_id} hover onClick={() => navigate(to)} sx={{ cursor: 'pointer' }}>
                          <TableCell>
                            <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 500 }} onClick={e => e.stopPropagation()}>
                              {c.chain_name}
                            </Link>
                            <Typography variant="caption" color="text.secondary" component="div">
                              {c.customer_tag || c.customer_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{modeLabel(c.mode, c.preset)}</TableCell>
                          <TableCell align="right">
                            <LimitCell now={c.limit_now_s} applied={c.applied_limit_s} note={c.limit_note} />
                          </TableCell>
                          <TableCell align="right">{fmtNum(c.runs)}</TableCell>
                          <TableCell align="right">
                            <Delta value={fmtNum(c.cut_offs)} delta={fmtPct(c.cut_off_share)} color={(c.cut_off_share || 0) >= 0.05 ? 'error.main' : 'text.secondary'} />
                          </TableCell>
                          <TableCell align="right">{fmtNum(c.completed_with_backup_step)}</TableCell>
                          <TableCell align="right">
                            <HeadroomCell p95={c.p95_ms} headroom={c.headroom_s} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </TabFrame>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminInsights: React.FC = () => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => adminInsightsApi(getIdToken), [getIdToken]);
  const [params, setParams] = useSearchParams();
  const [reload, setReload] = useState(0);
  const tab: TabKey = TABS.some(t => t.key === params.get('tab')) ? (params.get('tab') as TabKey) : 'retired';
  const requestedDays = Number(params.get('days'));
  const days = WINDOWS.includes(requestedDays) ? requestedDays : 7;

  const update = (key: string, value: string) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Platform Insights</Typography>
          <Typography variant="body2" color="text.secondary">
            Across all customers: retired models still in use, provider health, customers getting worse, time-limit cut-offs, and tested accuracy of
            the platform routes. Refreshed every 5 minutes.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && update('days', String(v))}>
            {WINDOWS.map(d => (
              <ToggleButton key={d} value={d}>
                {d === 1 ? '24h' : `${d}d`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Reload (results are cached for up to 5 minutes)">
            <IconButton onClick={() => setReload(r => r + 1)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Tabs value={tab} onChange={(_, v) => update('tab', v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" allowScrollButtonsMobile>
        {TABS.map(t => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>
      {tab === 'retired' && <RetiredTab api={api} days={days} reload={reload} />}
      {tab === 'providers' && <ProvidersTab api={api} days={days} reload={reload} />}
      {tab === 'customers' && <CustomersTab api={api} days={days} reload={reload} />}
      {tab === 'limits' && <LimitsTab api={api} days={days} reload={reload} />}
      {tab === 'accuracy' && <AccuracyTab reload={reload} />}
    </Box>
  );
};

export default AdminInsights;
